import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tag} from '@/components/ui/tag'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {KairosMark} from '@/components/kairos-mark'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {useRegistrationLink} from '@/hooks/sgsst/riesgos/use-entrevista'

const FALTANTES_PAGE_SIZE = 20

type FaltantesFiltro = 'todas' | 'sin_ficha' | 'sin_entrevista'

/** La lista sale de la misma cuenta del resumen, nunca de otra consulta. */
function FaltantesDialog({
  faltantes,
  open,
  onOpenChange,
}: {
  faltantes: Employee[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const [filtro, setFiltro] = useState<FaltantesFiltro>('todas')
  const sinFicha = faltantes.filter((e) => e.onboarding_estado === 'registro_basico')
  const sinEntrevista = faltantes.filter((e) => e.onboarding_estado !== 'registro_basico')
  const visto = filtro === 'sin_ficha' ? sinFicha : filtro === 'sin_entrevista' ? sinEntrevista : faltantes
  const visibles = visto.slice((page - 1) * FALTANTES_PAGE_SIZE, page * FALTANTES_PAGE_SIZE)
  const opciones: OpcionEstado<FaltantesFiltro>[] = [
    {valor: 'todas', etiqueta: t('registration.faltantesFiltro.todas'), conteo: faltantes.length},
    {valor: 'sin_ficha', etiqueta: t('registration.faltanteSinFicha'), conteo: sinFicha.length},
    {valor: 'sin_entrevista', etiqueta: t('registration.faltanteSinEntrevista'), conteo: sinEntrevista.length},
  ]
  function elegir(next: FaltantesFiltro) {
    setFiltro(next)
    setPage(1)
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('registration.faltantesTitle')}</DialogTitle>
        </DialogHeader>
        <FiltrosDeEstado
          opciones={opciones}
          valor={filtro}
          onValor={elegir}
          testIdPrefijo="entrevista-faltantes-filtro"
        />
        <ul className="divide-y divide-border" data-testid="entrevista-faltantes">
          {visibles.map((e) => (
            <li
              key={e.id}
              className="flex items-center justify-between gap-3 py-2"
              data-testid={`entrevista-faltante-${e.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {e.name} {e.last_name}
                </p>
                {e.positions.length > 0 && (
                  <p className="truncate text-xs text-muted-foreground">{e.positions.map((p) => p.name).join(' · ')}</p>
                )}
              </div>
              <Tag tone="amber" className="shrink-0">
                {e.onboarding_estado === 'registro_basico'
                  ? t('registration.faltanteSinFicha')
                  : t('registration.faltanteSinEntrevista')}
              </Tag>
            </li>
          ))}
        </ul>
        {visto.length > FALTANTES_PAGE_SIZE && (
          <PaginationBar page={page} pageSize={FALTANTES_PAGE_SIZE} total={visto.length} onPageChange={setPage} />
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Excluye los registros pendientes de aprobación. `canRemind` es solo del dueño (identity-access-spec
 * §1a.3): sin él la consulta del enlace ni se dispara. */
export function EntrevistaCompletionSummary({businessId, canRemind}: {businessId: string; canRemind: boolean}) {
  const {t} = useTranslation()
  const {data: employees} = useAllEmployees(businessId)
  const {data: link} = useRegistrationLink(canRemind ? businessId : undefined)
  const [copied, setCopied] = useState(false)
  const [verFaltantes, setVerFaltantes] = useState(false)

  if (!employees) return null

  const counted = employees.filter((e) => e.status === 'active' && !e.onboarding_pendiente_aprobacion)
  const faltantes = counted.filter((e) => e.onboarding_estado !== 'entrevista_completa')
  const done = counted.length - faltantes.length

  async function remind() {
    if (!link) return
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/registro/${link.token}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="surface flex items-center justify-between gap-4 p-5" data-testid="entrevista-completion-summary">
      <div className="flex items-center gap-3">
        <KairosMark />
        <div>
          <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('registration.summaryTitle')}
          </p>
          {faltantes.length > 0 ? (
            <button
              type="button"
              className="mt-0.5 text-left text-sm font-medium text-foreground underline decoration-dotted underline-offset-4 hover:text-primary"
              data-testid="entrevista-completion-count"
              onClick={() => setVerFaltantes(true)}>
              {t('registration.summaryCount', {done, total: counted.length})}
            </button>
          ) : (
            <p className="mt-0.5 text-sm font-medium text-foreground" data-testid="entrevista-completion-count">
              {counted.length === 0
                ? t('registration.summaryEmpty')
                : t('registration.summaryCount', {done, total: counted.length})}
            </p>
          )}
        </div>
      </div>
      <FaltantesDialog faltantes={faltantes} open={verFaltantes} onOpenChange={setVerFaltantes} />
      {/* Con la nómina en cero no hay botón: el enlace ya lo ofrece la tarjeta de arranque del SG-SST (U7). */}
      {canRemind && counted.length > 0 && done < counted.length && (
        <Button size="sm" variant="outline" onClick={remind} data-testid="entrevista-completion-remind">
          {copied ? t('registration.reminderCopied') : t('registration.reminderCta')}
        </Button>
      )}
    </div>
  )
}
